import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link2, Loader2, Building2, User as UserIcon } from 'lucide-react';
import type { LinkedClient } from '../../lib/mail/threadLink';
import { findPersonByEmail, type ThreadPerson } from '../../lib/mail/threadPersonLookup';

interface Props {
  client: LinkedClient;
  /** Indirizzo della controparte esterna del thread, per trovare la persona. */
  senderEmail: string | null;
  organizationId: string;
  onUnlink: () => Promise<void> | void;
}

/**
 * Riga sotto l'oggetto: a chi e' collegato il thread. Prima di questa riga il
 * collegamento era visibile solo nel pulsante in alto a destra, che mostra una sola
 * entita' e non permette di aprire la scheda.
 */
export function ThreadLinkBar({ client, senderEmail, organizationId, onUnlink }: Props) {
  const navigate = useNavigate();
  const [person, setPerson] = useState<ThreadPerson | null>(null);
  const [unlinking, setUnlinking] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setPerson(null);
    if (!senderEmail) return;
    (async () => {
      const found = await findPersonByEmail(organizationId, senderEmail);
      if (!cancelled) setPerson(found);
    })();
    return () => { cancelled = true; };
  }, [organizationId, senderEmail]);

  const entityName = client.business_name || client.contact_name || 'Scheda collegata';
  // La persona non si ripete quando e` gia` lei l'entita` collegata al thread.
  const showPerson = person && person.id !== client.id;

  const handleUnlink = async () => {
    setUnlinking(true);
    try {
      await onUnlink();
    } finally {
      setUnlinking(false);
    }
  };

  return (
    <div className="mail-link-section flex items-center gap-2 px-4 py-1.5 border-b border-[rgba(55,53,47,0.06)] dark:border-gray-800/60 bg-[#2383E2]/[0.04] dark:bg-[#2383E2]/[0.08]">
      <Link2 className="h-3.5 w-3.5 text-[#2383E2] flex-shrink-0" />
      <span className="text-xs text-[#787774] dark:text-white/40 flex-shrink-0">Collegato a:</span>

      <div className="flex items-center gap-1.5 min-w-0 flex-1">
        <button
          onClick={() => navigate(`/clients/${client.id}`)}
          className="inline-flex items-center gap-1 px-1 -mx-1 rounded text-xs font-medium text-[#2383E2] hover:bg-[#2383E2]/10 transition-colors truncate max-w-[240px]"
          title={`Apri la scheda di ${entityName}`}
        >
          <Building2 className="h-3 w-3 flex-shrink-0" />
          <span className="truncate">{entityName}</span>
        </button>

        {showPerson && (
          <>
            <span className="text-[#787774] dark:text-white/30">·</span>
            <button
              onClick={() => navigate(`/clients/${person.id}`)}
              className="inline-flex items-center gap-1 px-1 -mx-1 rounded text-xs font-medium text-[#2383E2] hover:bg-[#2383E2]/10 transition-colors truncate max-w-[200px]"
              title={`Apri la scheda di ${person.business_name}`}
            >
              <UserIcon className="h-3 w-3 flex-shrink-0" />
              <span className="truncate">{person.business_name}</span>
            </button>
          </>
        )}
      </div>

      <button
        onClick={handleUnlink}
        disabled={unlinking}
        className="flex-shrink-0 inline-flex items-center gap-1 text-xs text-[#787774] dark:text-white/50 hover:text-[#37352F] dark:hover:text-white transition-colors disabled:opacity-50"
        title="Rimuove solo il collegamento dell'email, non tocca le anagrafiche"
      >
        {unlinking && <Loader2 className="h-3 w-3 animate-spin" />}
        Scollega
      </button>
    </div>
  );
}
